<script setup>
import { computed } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  isAdmin: { type: Boolean, default: false },
  registerUrl: { type: String, default: '/register' },
  footerLinkUrl: { type: String, default: '' },
  footerLinkLabel: { type: String, default: '' },
})

const currentYear = new Date().getFullYear()
const footerLink = computed(() => {
  const url = props.footerLinkUrl || props.registerUrl

  return url
    ? { url, label: props.footerLinkLabel || 'Registration' }
    : null
})
</script>

<template>
  <div class="classic-shell" :class="{ 'classic-shell--admin': isAdmin }">
    <header class="classic-shell__header">
      <a class="classic-shell__logo" href="/">
        <img
          :src="isAdmin ? '/images/global/admin_logo-remonihealth.svg' : '/images/global/logo-remonihealth.svg'"
          alt="RemoniHealth"
        >
      </a>
    </header>

    <main class="classic-auth">
      <section class="classic-auth__card" aria-labelledby="classic-auth-title">
        <header class="classic-auth__header">
          <h1 id="classic-auth-title" class="classic-auth__title">
            <slot name="title">{{ title }}</slot>
          </h1>
          <p v-if="subtitle || $slots.subtitle" class="classic-auth__subtitle">
            <slot name="subtitle">{{ subtitle }}</slot>
          </p>
        </header>

        <div class="classic-auth__body">
          <slot />
        </div>

        <footer v-if="$slots.footer" class="classic-auth__footer">
          <slot name="footer" />
        </footer>
      </section>
    </main>

    <footer class="classic-shell__footer">
      <p>©{{ currentYear }}. RemoniHealth. All Rights Reserved.</p>
      <a v-if="!isAdmin && footerLink" :href="footerLink.url">{{ footerLink.label }}</a>
      <nav class="classic-shell__social" aria-label="Social media">
        <a href="https://www.facebook.com/keepyoursight/" aria-label="Facebook"><img src="/images/icons/facebook.svg" alt=""></a>
        <a href="https://twitter.com/Keep_Your_Sight" aria-label="X"><img src="/images/icons/twitter.svg" alt=""></a>
        <a href="https://www.instagram.com/kys_vision/" aria-label="Instagram"><img src="/images/icons/instagram.svg" alt=""></a>
        <a href="https://www.linkedin.com/company/keepyoursight/?viewAsMember=true" aria-label="LinkedIn"><img src="/images/icons/linkedin.svg" alt=""></a>
      </nav>
    </footer>
  </div>
</template>

<style scoped>
@font-face {
  font-display: swap;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  src:
    url('/fonts/poppins/regular/poppins-regular.woff2') format('woff2'),
    url('/fonts/poppins/regular/poppins-regular.woff') format('woff');
}

@font-face {
  font-display: swap;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  src:
    url('/fonts/poppins/medium/poppins-medium.woff2') format('woff2'),
    url('/fonts/poppins/medium/poppins-medium.woff') format('woff');
}

@font-face {
  font-display: swap;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 600;
  src:
    url('/fonts/poppins/semi-bold/poppins-semibold.woff2') format('woff2'),
    url('/fonts/poppins/semi-bold/poppins-semibold.woff') format('woff');
}

@font-face {
  font-display: swap;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 700;
  src:
    url('/fonts/poppins/bold/poppins-bold.woff2') format('woff2'),
    url('/fonts/poppins/bold/poppins-bold.woff') format('woff');
}

.classic-shell {
  --auth-accent: #ff7f5d;
  --auth-accent-dark: #ff6238;
  --auth-accent-light: #fef3ec;
  --auth-border: #c6c6c6;
  --auth-card-bg: #fff;
  --auth-error-bg: #fff;
  --auth-error-text: #dc3545;
  --auth-input-bg: transparent;
  --auth-input-border: #c6c6c6;
  --auth-input-placeholder: #7c7c7c;
  --auth-text: #333;
  --auth-text-muted: #7c7c7c;
  background: #fff;
  box-sizing: border-box;
  color: #333;
  color-scheme: light;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', 'poppinsregular', Arial, sans-serif;
  min-height: 100vh;
  min-height: 100dvh;
  padding-top: 80px;
}

.classic-shell--admin {
  --auth-accent: #56dbcb;
  --auth-accent-dark: #39d5c2;
  --auth-accent-light: #ecfdfb;
}

.classic-shell__header {
  align-items: center;
  background: #fff;
  box-shadow: 0 4px 16px rgba(11, 40, 61, 0.08);
  display: flex;
  height: 80px;
  left: 0;
  padding: 0 max(20px, calc((100vw - 1140px) / 2));
  position: fixed;
  right: 0;
  top: 0;
  z-index: 101;
}

.classic-shell__logo {
  display: block;
  max-width: 144px;
}

.classic-shell__logo img {
  display: block;
  height: auto;
  max-width: 100%;
}

.classic-auth {
  align-items: center;
  background: #f8f7fb url('/images/auth/bg.png') center -85px / 100% auto no-repeat;
  box-sizing: border-box;
  display: flex;
  flex: 1 0 auto;
  justify-content: center;
  padding: 24px 20px 16px;
}

.classic-shell--admin .classic-auth {
  background-image: url('/images/admin/bg.png');
}

.classic-auth__card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 0 10px 2px rgba(34, 60, 80, 0.05);
  color: #212529;
  overflow: hidden;
  width: min(100%, 460px);
}

.classic-auth__header {
  background: #fff url('/images/auth/form-topwave.png') 0 top / 160% auto no-repeat;
  padding: 40px 40px 20px;
  text-align: center;
}

.classic-shell--admin .classic-auth__header {
  background-image: url('/images/admin/form-wave.png');
}

.classic-auth__title {
  font-size: 35px;
  font-weight: 600;
  line-height: 1.42;
  margin: 0 0 20px;
}

.classic-auth__title :deep(.classic-auth__accent),
.classic-auth__subtitle :deep(.classic-auth__accent) {
  color: var(--auth-accent);
}

.classic-auth__subtitle {
  color: #7c7c7c;
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.classic-auth__body {
  padding: 0 40px 40px;
}

.classic-auth__body :deep(input),
.classic-auth__body :deep(select),
.classic-auth__body :deep(textarea) {
  caret-color: #333;
  color: #7c7c7c;
  font-family: inherit;
  -webkit-text-fill-color: #7c7c7c;
}

.classic-auth__body :deep(button) {
  font-family: inherit;
}

.classic-auth__body :deep(input::placeholder),
.classic-auth__body :deep(textarea::placeholder) {
  color: #7c7c7c;
  opacity: 0.75;
  -webkit-text-fill-color: #7c7c7c;
}

.classic-auth__body :deep(input:-webkit-autofill),
.classic-auth__body :deep(input:-webkit-autofill:hover),
.classic-auth__body :deep(input:-webkit-autofill:focus),
.classic-auth__body :deep(textarea:-webkit-autofill),
.classic-auth__body :deep(select:-webkit-autofill) {
  -webkit-box-shadow: 0 0 0 1000px #fff inset;
  -webkit-text-fill-color: #7c7c7c;
}

.classic-auth__footer {
  padding: 0 40px 40px;
  text-align: center;
}

.classic-auth__body :deep(.auth-register__form) {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.classic-auth__body :deep(.auth-register__form .auth-login__field) {
  margin-bottom: 30px;
}

.classic-auth__body :deep(.auth-login__field) {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.classic-auth__body :deep(.auth-login__label) {
  color: #333;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.classic-auth__body :deep(.auth-login__input),
.classic-auth__body :deep(.auth-login__select) {
  background: transparent;
  border: 0;
  border-bottom: 1px solid #c6c6c6;
  border-radius: 0;
  box-sizing: border-box;
  color: #7c7c7c;
  font: inherit;
  min-height: 46px;
  padding: 10px 0;
  width: 100%;
}

.classic-auth__body :deep(.auth-login__input:focus),
.classic-auth__body :deep(.auth-login__select:focus) {
  border-color: #ff7f5d;
  box-shadow: none;
  outline: none;
}

.classic-auth__body :deep(.auth-login__input--error) {
  border-color: #dc3545;
}

.classic-auth__body :deep(.auth-login__error),
.classic-auth__body :deep(.auth-login__error-block) {
  color: #dc3545;
  font-size: 13px;
}

.classic-auth__body :deep(.auth-login__actions) {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
}

.classic-auth__body :deep(.auth-login__btn) {
  border-radius: 25px;
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  min-height: auto;
  min-width: 230px;
  padding: 12px 20px;
  width: auto;
}

.classic-auth__body :deep(.auth-login__btn--primary) {
  background: #ff7f5d;
  border-color: #ff7f5d;
  color: #fff;
}

.classic-shell--admin .classic-auth__body :deep(.auth-login__btn--primary) {
  background: #56dbcb;
  border-color: #56dbcb;
}

.classic-auth__body :deep(.auth-login__btn--ghost),
.classic-auth__body :deep(.auth-login__link) {
  color: #564495;
  font-size: 14px;
}

.classic-auth__body :deep(.auth-login__panel-subtitle) {
  color: #7c7c7c;
  font-size: 16px;
  margin-bottom: 20px;
}

.classic-auth__body :deep(.auth-register__video-link) {
  border-bottom: 1px solid currentColor;
  color: #ff6339;
  font-size: 18px;
  font-weight: 600;
  text-decoration: none;
}

.classic-auth__body :deep(.auth-login__checkbox) {
  font-size: 14px;
  line-height: 1.5;
}

.classic-auth__body :deep(.auth-register__field-with-link) {
  margin-bottom: 16px;
}

.classic-auth__body :deep(.auth-register__field-with-link .auth-login__field) {
  margin-bottom: 4px;
}

.classic-auth__body :deep(.auth-login__password-wrap) {
  position: relative;
}

.classic-auth__body :deep(.auth-login__password-toggle) {
  background: transparent;
  border: 0;
  color: #7c7c7c;
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.classic-auth__body :deep(.auth-login__classic-password-eye) {
  display: block;
  height: auto;
  width: 25px;
}

.classic-shell--admin .classic-auth__body :deep(.auth-login__input:focus),
.classic-shell--admin .classic-auth__body :deep(.auth-login__select:focus) {
  border-color: #56dbcb;
}

.classic-shell__footer {
  align-items: center;
  box-sizing: border-box;
  display: grid;
  flex: 0 0 45px;
  font-size: 12px;
  grid-template-columns: 1fr 1fr 1fr;
  margin: 0 auto;
  max-width: 1140px;
  padding: 0 20px;
  width: 100%;
}

.classic-shell__footer p {
  color: #7c7c7c;
  margin: 0;
}

.classic-shell__footer > a {
  color: #ff7f5d;
  font-size: 18px;
  font-weight: 600;
  justify-self: center;
  text-decoration: none;
}

.classic-shell__social {
  display: flex;
  gap: 15px;
  grid-column: 3;
  justify-self: end;
}

.classic-shell__social img {
  height: 22px;
  width: 22px;
}

@media (max-width: 576px) {
  .classic-auth {
    background-size: auto 360px;
    padding: 40px 16px 24px;
  }

  .classic-shell__footer {
    gap: 12px;
    grid-template-columns: 1fr;
    justify-items: center;
    padding-bottom: 24px;
    padding-top: 24px;
  }

  .classic-shell__social {
    grid-column: 1;
    justify-self: center;
  }

  .classic-auth__header,
  .classic-auth__body,
  .classic-auth__footer {
    padding-left: 20px;
    padding-right: 20px;
  }

  .classic-auth__title {
    font-size: 29px;
    line-height: 1.3;
  }
}
</style>
